import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ActionIcon,
  Alert,
  Badge,
  Checkbox,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';

import { ExerciseAssetsPanel } from '@components/Asset';
import { BasicButton } from '@components/Button';
import { PageTitle } from '@components/shared';
import { ExerciseTagsPanel, Taggit } from '@components/Tag';
import entityColor from '@util/entityColor';
import entityIcon from '@util/entityIcon';
import { toErrorMessage } from '@util/toErrorMessage';

export default function AdminExerciseDetail() {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [usesWeight, setUsesWeight] = useState(true);
  const [usesReps, setUsesReps] = useState(true);
  const [usesDuration, setUsesDuration] = useState(false);
  const [usesDistance, setUsesDistance] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const exerciseQuery = useQuery({
    queryKey: ['admin-exercise', exerciseId],
    queryFn: async ({ signal }) => {
      const { data } = await axios.get(`/exercises/${exerciseId}/`, { signal });
      return data.response?.[0];
    },
    enabled: Boolean(exerciseId),
  });

  const varietiesQuery = useQuery({
    queryKey: ['tag-varieties'],
    queryFn: async ({ signal }) => {
      const { data } = await axios.get(`/retrieve-tag-varieties/`, { signal });
      return data.response || [];
    },
  });

  const exercise = exerciseQuery.data;
  const varieties = varietiesQuery.data || [];
  const isDeleted = exercise?.status_formatted === 'deleted';
  const isActive = exercise?.status_formatted === 'active';
  const isInactive = exercise?.status_formatted === 'inactive';
  const tagsByVariety = exercise?.tags_by_variety || {};

  useEffect(() => {
    if (!exercise) return;
    setName(exercise.name || '');
    setDescription(exercise.description || '');
    setUsesWeight(exercise.uses_weight ?? true);
    setUsesReps(exercise.uses_reps ?? true);
    setUsesDuration(exercise.uses_duration ?? false);
    setUsesDistance(exercise.uses_distance ?? false);
    setConfirmDelete(false);
  }, [exercise]);

  function reloadExercise() {
    queryClient.invalidateQueries({ queryKey: ['admin-exercise', exerciseId] });
    queryClient.invalidateQueries({ queryKey: ['admin-exercises-picker'] });
    queryClient.invalidateQueries({ queryKey: ['paginated-list'] });
  }

  function onSave() {
    if (!exerciseId || isDeleted) return;
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    axios
      .put(`/exercises/${exerciseId}/`, {
        name: name.trim(),
        description: description.trim() || null,
        uses_weight: usesWeight,
        uses_reps: usesReps,
        uses_duration: usesDuration,
        uses_distance: usesDistance,
      })
      .then(() => {
        toast.success('Exercise saved');
        reloadExercise();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setSaving(false));
  }

  function onActivate() {
    setStatusBusy(true);
    axios
      .post(`/exercises/${exerciseId}/activate/`, {})
      .then(() => {
        toast.success('Exercise activated');
        reloadExercise();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setStatusBusy(false));
  }

  function onInactivate() {
    setStatusBusy(true);
    axios
      .post(`/exercises/${exerciseId}/inactivate/`, {})
      .then(() => {
        toast.success('Exercise inactivated');
        reloadExercise();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setStatusBusy(false));
  }

  function onDelete() {
    setStatusBusy(true);
    axios
      .delete(`/exercises/${exerciseId}/`)
      .then(() => {
        toast.success('Exercise deleted');
        queryClient.invalidateQueries({ queryKey: ['paginated-list'] });
        queryClient.invalidateQueries({ queryKey: ['admin-exercises-picker'] });
        navigate('/exercises');
      })
      .catch((error) => {
        toast.error(toErrorMessage(error));
        setStatusBusy(false);
        setConfirmDelete(false);
      });
  }

  const cuesBlock = (
    <Stack gap={4}>
      <Text fw={700}>Coaching cues</Text>
      <Text size="sm" c="dimmed">
        KEY POINTS shown on the athlete workout screen.
      </Text>
      <Taggit
        reqInfo={{
          exercise_id: exerciseId,
          variety: 'coaching_cues',
        }}
        fetchData={reloadExercise}
        readOnly={isDeleted}
      />
    </Stack>
  );

  if (exerciseQuery.isLoading) {
    return (
      <Group gap="sm">
        <Loader size="sm" type="dots" />
        <Text c="dimmed">Loading exercise…</Text>
      </Group>
    );
  }

  if (exerciseQuery.isError || !exercise) {
    return (
      <Alert variant="light" color="yellow" title="Oops!">
        {exerciseQuery.isError
          ? toErrorMessage(exerciseQuery.error)
          : 'Exercise not found'}
      </Alert>
    );
  }

  return (
    <div>
      <Group gap="xs" mb="sm">
        <ActionIcon
          variant="subtle"
          color="gray"
          aria-label="Back"
          onClick={() => navigate('/exercises')}
        >
          <IconArrowLeft size={18} />
        </ActionIcon>
        <Text size="sm" c="dimmed">
          Exercise
        </Text>
      </Group>

      <PageTitle
        title={name || exercise.name}
        subtitle="Identity, coaching cues, and library tags"
        icon={entityIcon.exercise()}
        iconColor={entityColor.exercise}
      />

      <Stack gap="md" mt="md">
        <Group gap="xs">
          <Badge
            size="sm"
            variant="light"
            color={
              isActive ? 'teal' : isInactive ? 'gray' : isDeleted ? 'red' : 'blue'
            }
          >
            {exercise.status_formatted}
          </Badge>
        </Group>

        {isDeleted ? (
          <>
            <Alert variant="light" color="red" title="Deleted">
              This exercise is soft-deleted and cannot be edited.
            </Alert>
            <Paper p="md" withBorder radius="sm">
              {cuesBlock}
            </Paper>
          </>
        ) : (
          <Paper p="md" withBorder radius="sm">
            <Stack gap="md">
              <TextInput
                label="Name"
                value={name}
                onChange={(event) => setName(event.currentTarget.value)}
                required
                disabled={saving || statusBusy}
              />
              <Textarea
                label="Description"
                value={description}
                onChange={(event) => setDescription(event.currentTarget.value)}
                minRows={2}
                autosize
                disabled={saving || statusBusy}
              />
              {cuesBlock}
              <Stack gap="xs">
                <Text fw={700}>Prescription fields</Text>
                <Text size="sm" c="dimmed">
                  Controls which inputs appear when this exercise is added to a
                  workout.
                </Text>
                <Checkbox
                  label="Uses weight (lb / % of max)"
                  checked={usesWeight}
                  onChange={(event) => setUsesWeight(event.currentTarget.checked)}
                  disabled={saving || statusBusy}
                />
                <Checkbox
                  label="Uses reps"
                  checked={usesReps}
                  onChange={(event) => setUsesReps(event.currentTarget.checked)}
                  disabled={saving || statusBusy}
                />
                <Checkbox
                  label="Uses duration"
                  checked={usesDuration}
                  onChange={(event) =>
                    setUsesDuration(event.currentTarget.checked)
                  }
                  disabled={saving || statusBusy}
                />
                <Checkbox
                  label="Uses distance"
                  checked={usesDistance}
                  onChange={(event) =>
                    setUsesDistance(event.currentTarget.checked)
                  }
                  disabled={saving || statusBusy}
                />
              </Stack>

              <Group>
                <BasicButton
                  text={saving ? 'Saving…' : 'Save'}
                  onClick={onSave}
                  loading={saving}
                  disabled={saving || statusBusy || !name.trim()}
                  color={entityColor.exercise}
                  variant="filled"
                />
                {!isActive && (
                  <BasicButton
                    text="Activate"
                    onClick={onActivate}
                    loading={statusBusy}
                    disabled={statusBusy || saving}
                    color="teal"
                    variant="light"
                  />
                )}
                {!isInactive && (
                  <BasicButton
                    text="Inactivate"
                    onClick={onInactivate}
                    loading={statusBusy}
                    disabled={statusBusy || saving}
                    color="gray"
                    variant="outline"
                  />
                )}
              </Group>

              <Group mt="xs">
                {confirmDelete ? (
                  <>
                    <Text size="sm" fw={700}>
                      Delete this exercise?
                    </Text>
                    <BasicButton
                      text="Confirm delete"
                      onClick={onDelete}
                      loading={statusBusy}
                      disabled={statusBusy}
                      color="red"
                      variant="filled"
                    />
                    <BasicButton
                      text="Cancel"
                      onClick={() => setConfirmDelete(false)}
                      disabled={statusBusy}
                      color="gray"
                      variant="outline"
                    />
                  </>
                ) : (
                  <BasicButton
                    text="Delete exercise"
                    onClick={() => setConfirmDelete(true)}
                    disabled={statusBusy || saving}
                    color="red"
                    variant="outline"
                  />
                )}
              </Group>
            </Stack>
          </Paper>
        )}

        <ExerciseAssetsPanel
          exerciseId={exerciseId}
          assets={exercise.assets || []}
          readOnly={isDeleted}
          onChanged={reloadExercise}
        />

        {varietiesQuery.isError ? (
          <Alert variant="light" color="yellow" title="Tags unavailable">
            {toErrorMessage(varietiesQuery.error)}
          </Alert>
        ) : (
          <ExerciseTagsPanel
            exerciseId={exerciseId}
            tagsByVariety={tagsByVariety}
            varieties={varieties}
            readOnly={isDeleted}
            onChanged={reloadExercise}
          />
        )}
      </Stack>
    </div>
  );
}
